import { useTenant } from "@/hooks/useTenant";
import { portalFetch } from "@/lib/portalApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export function useStarredDashboards() {
  const { currentTenantId } = useTenant();

  const { data, isLoading } = useQuery({
    queryKey: ["portal-starred", currentTenantId],
    queryFn: () => portalFetch<string[]>(`/api/portal/tenants/${currentTenantId}/starred`),
    enabled: !!currentTenantId,
  });

  return { starredIds: data ?? [], isLoading };
}

export function useToggleStar() {
  const { currentTenantId } = useTenant();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ dashboardId, starred }: { dashboardId: string; starred: boolean }) => {
      if (starred) {
        await portalFetch(`/api/portal/tenants/${currentTenantId}/starred`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tenantId: currentTenantId, dashboardId }),
        });
      } else {
        await portalFetch(`/api/portal/tenants/${currentTenantId}/starred/${dashboardId}`, {
          method: "DELETE",
        });
      }
    },
    onMutate: async ({ dashboardId, starred }) => {
      await qc.cancelQueries({ queryKey: ["portal-starred", currentTenantId] });
      const previous = qc.getQueryData<string[]>(["portal-starred", currentTenantId]);

      qc.setQueryData<string[]>(["portal-starred", currentTenantId], (old = []) =>
        starred ? [...old, dashboardId] : old.filter((id) => id !== dashboardId),
      );

      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(["portal-starred", currentTenantId], ctx.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["portal-starred", currentTenantId] });
    },
  });
}
